/**
 * `chat` store —— 会话列表、消息分页缓存、未读总数。
 *
 * 只存**非敏感的渲染态**：`ciphertext/iv/signature` 虽落库但明文 `body` 仅供本地渲染
 * （取舍见 ARCH §4.5 注）。密钥永不进 store。
 */

import { create } from 'zustand';
import type { Contact, Message, MsgStatus } from '@shared/types';

export interface ChatState {
  contacts: Contact[];
  activePeerId: string | null;
  /** peerId → 消息（按 ts 升序） */
  messagesByPeer: Record<string, Message[]>;
  unreadTotal: number;
  loadingMore: boolean;

  setContacts(contacts: Contact[]): void;
  upsertContact(contact: Contact): void;
  removeContact(peerId: string): void;
  setActive(peerId: string | null): void;
  /** `append=false` 表示替换（首屏），`true` 表示追加（上拉加载） */
  setMessages(peerId: string, messages: Message[], append?: boolean): void;
  appendMessage(peerId: string, message: Message): void;
  updateStatus(peerId: string, msgId: string, status: MsgStatus): void;
  setLoadingMore(loading: boolean): void;
  reset(): void;
}

/** 去重 + 按 ts 升序合并，保证 500 条会话渲染稳定。 */
function mergeSorted(list: Message[], incoming: Message[]): Message[] {
  const map = new Map<string, Message>();
  for (const m of list) map.set(m.msgId, m);
  for (const m of incoming) {
    const prev = map.get(m.msgId);
    // 后到的状态更新优先（sending → sent → delivered → read）
    map.set(m.msgId, prev ? { ...prev, ...m } : m);
  }
  return [...map.values()].sort((a, b) => a.ts - b.ts);
}

export const useChatStore = create<ChatState>((set, get) => ({
  contacts: [],
  activePeerId: null,
  messagesByPeer: {},
  unreadTotal: 0,
  loadingMore: false,

  setContacts: (contacts) =>
    set({
      contacts,
      unreadTotal: contacts.reduce((sum, c) => sum + (c.blocked ? 0 : c.unread), 0),
    }),

  upsertContact: (contact) => {
    const others = get().contacts.filter((c) => c.peerId !== contact.peerId);
    const next = [contact, ...others].sort((a, b) => (b.lastTs ?? b.updatedAt) - (a.lastTs ?? a.updatedAt));
    set({
      contacts: next,
      unreadTotal: next.reduce((sum, c) => sum + (c.blocked ? 0 : c.unread), 0),
    });
  },

  removeContact: (peerId) => {
    const contacts = get().contacts.filter((c) => c.peerId !== peerId);
    const messagesByPeer = { ...get().messagesByPeer };
    delete messagesByPeer[peerId];
    set({
      contacts,
      messagesByPeer,
      unreadTotal: contacts.reduce((sum, c) => sum + (c.blocked ? 0 : c.unread), 0),
      activePeerId: get().activePeerId === peerId ? null : get().activePeerId,
    });
  },

  setActive: (peerId) => set({ activePeerId: peerId }),

  setMessages: (peerId, messages, append = false) =>
    set((state) => {
      const prev = state.messagesByPeer[peerId] ?? [];
      const next = append ? mergeSorted(messages, prev) : mergeSorted([], messages);
      return { messagesByPeer: { ...state.messagesByPeer, [peerId]: next } };
    }),

  appendMessage: (peerId, message) =>
    set((state) => {
      const prev = state.messagesByPeer[peerId] ?? [];
      if (prev.some((m) => m.msgId === message.msgId)) {
        // 已存在则只更新状态
        return {
          messagesByPeer: {
            ...state.messagesByPeer,
            [peerId]: prev.map((m) => (m.msgId === message.msgId ? { ...m, ...message } : m)),
          },
        };
      }
      return { messagesByPeer: { ...state.messagesByPeer, [peerId]: [...prev, message] } };
    }),

  updateStatus: (peerId, msgId, status) =>
    set((state) => {
      const prev = state.messagesByPeer[peerId];
      if (!prev) return state;
      return {
        messagesByPeer: {
          ...state.messagesByPeer,
          [peerId]: prev.map((m) => (m.msgId === msgId ? { ...m, status } : m)),
        },
      };
    }),

  setLoadingMore: (loading) => set({ loadingMore: loading }),

  reset: () => set({ contacts: [], activePeerId: null, messagesByPeer: {}, unreadTotal: 0, loadingMore: false }),
}));

export default useChatStore;
